import React, { useState } from "react";
import { X, FileText, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ModalPortal } from "./ModalPortal";

export const CoaRequestModal = ({ product, open, onClose }) => {
    const [form, setForm] = useState({ name: "", email: "", note: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !product) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/coa-request", {
                product_slug: product.slug,
                product_name: product.name,
                customer_email: form.email,
                customer_name: form.name || null,
                note: form.note || null,
            });
            setDone(true);
            toast.success("COA request received — we'll email you shortly.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((e) => e.msg).join(" ") : (detail || "Could not send request"));
        }
        setSubmitting(false);
    };

    const close = () => {
        setForm({ name: "", email: "", note: "" });
        setDone(false);
        onClose();
    };

    return (
        <ModalPortal>
        <div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
            data-testid="coa-request-modal"
        >
            <div
                className="glass-elevated rounded-2xl p-6 max-w-md w-full border border-[var(--gold)]/30"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <span className="eyebrow">// Compliance</span>
                        <h3 className="font-display text-2xl font-bold text-white tracking-tighter italic mt-1">
                            Request <span className="text-[var(--gold)]">COA</span>
                        </h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-1.5">
                            Manufacturer-supplied Certificate of Analysis for <strong className="text-white">{product.name}</strong>
                        </p>
                    </div>
                    <button
                        onClick={close}
                        className="p-2 text-[var(--text-muted)] hover:text-white"
                        aria-label="Close"
                        data-testid="coa-modal-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {done ? (
                    <div className="text-center py-6" data-testid="coa-modal-success">
                        <CheckCircle2 className="mx-auto text-emerald-300 mb-3" size={42} />
                        <p className="text-white font-display text-lg mb-1">Request sent</p>
                        <p className="text-sm text-[var(--text-secondary)] mb-5">
                            We&apos;ll email the manufacturer-supplied COA for {product.name} to <strong className="text-[var(--gold)]">{form.email}</strong> shortly.
                        </p>
                        <button onClick={close} className="btn-solid" data-testid="coa-modal-done">
                            Got it
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="coa-request-form">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Your email</label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="you@lab.com"
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                data-testid="coa-form-email"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Your name (optional)</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Dr. J. Smith"
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                data-testid="coa-form-name"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Note (optional)</label>
                            <textarea
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                rows={3}
                                placeholder="If you need a specific batch / order ref, mention it here."
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] resize-none"
                                data-testid="coa-form-note"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-solid w-full"
                            data-testid="coa-form-submit"
                        >
                            <Send size={14} /> {submitting ? "Sending…" : "Send request"}
                        </button>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center pt-1">
                            <FileText size={10} className="inline mr-1" />
                            Manufacturer-supplied COA · Batch-level
                        </p>
                    </form>
                )}
            </div>
        </div>
        </ModalPortal>
    );
};
